import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Users, DollarSign, Clock, AlertTriangle, Search, MoreVertical,
  UserCheck, UserX, KeyRound, Trash2, Wallet, Loader2, ArrowUpDown,
  TrendingUp, Activity, Download, ExternalLink, RefreshCw, FileCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PaginationControls } from "../components/Pagination";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

function formatCurrency(val) {
  return `$${(val || 0).toFixed(2)}`;
}

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const navigate = useNavigate();

  // Dialogs
  const [balanceDialog, setBalanceDialog] = useState({ open: false, user: null });
  const [balanceAmount, setBalanceAmount] = useState("");
  const [balanceReason, setBalanceReason] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, user: null, title: "", desc: "" });
  const [actionLoading, setActionLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/users/summary`),
      ]);
      setUsers(usersRes.data);
      setSummary(summaryRes.data);
    } catch (e) {
      toast.error("Failed to load user data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const filtered = users
    .filter((u) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.company || "").toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const aVal = a[sortField] ?? "";
      const bVal = b[sortField] ?? "";
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortDir === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

  const paginatedUsers = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const doAction = async (action, user) => {
    setActionLoading(true);
    try {
      if (action === "suspend") {
        await axios.post(`${API}/admin/users/${user.id}/suspend`);
        toast.success(`${user.name || user.email} suspended`);
      } else if (action === "activate") {
        await axios.post(`${API}/admin/users/${user.id}/activate`);
        toast.success(`${user.name || user.email} activated`);
      } else if (action === "reset-password") {
        const res = await axios.post(`${API}/admin/users/${user.id}/reset-password`);
        toast.success(res.data.message);
      } else if (action === "purge-calls") {
        const res = await axios.post(`${API}/admin/users/${user.id}/purge-calls`);
        toast.success(`Purged: ${res.data.calls_deleted} calls, ${res.data.publishers_deleted} publishers, ${res.data.callers_deleted} callers`);
      } else if (action === "delete") {
        await axios.delete(`${API}/admin/users/${user.id}`);
        toast.success(`${user.name || user.email} deleted`);
      }
      setConfirmDialog({ open: false, action: null, user: null, title: "", desc: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBalanceSubmit = async () => {
    const amount = parseFloat(balanceAmount);
    if (isNaN(amount) || amount === 0) { toast.error("Enter a valid amount"); return; }
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/users/${balanceDialog.user.id}/adjust-credits`, {
        amount, reason: balanceReason,
      });
      toast.success(`Minutes adjusted. New balance: ${res.data.new_credits?.toFixed(0)} minutes`);
      setBalanceDialog({ open: false, user: null });
      setBalanceAmount("");
      setBalanceReason("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to adjust balance");
    } finally {
      setActionLoading(false);
    }
  };

  const openConfirm = (action, user, title, desc) => {
    setConfirmDialog({ open: true, action, user, title, desc });
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const summaryCards = [
    { label: "Total Users", value: summary?.total_users || 0, icon: Users, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "Active Users", value: summary?.active_users || 0, icon: UserCheck, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    { label: "Minutes Purchased", value: summary?.total_credits_purchased || 0, icon: DollarSign, color: "text-green-400", bg: "bg-green-500/10" },
    { label: "Minutes Used", value: summary?.total_credits_used || 0, icon: TrendingUp, color: "text-amber-400", bg: "bg-amber-500/10" },
    { label: "Platform Minutes", value: `${summary?.total_minutes || 0}`, icon: Clock, color: "text-cyan-400", bg: "bg-cyan-500/10" },
    { label: "Low Minutes", value: summary?.low_balance_users || 0, icon: AlertTriangle, color: "text-red-400", bg: "bg-red-500/10" },
  ];

  const SortHeader = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-200 transition"
    >
      {children}
      <ArrowUpDown className="h-3 w-3 opacity-50" />
    </button>
  );

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>
            User Management
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Manage users, accounts, and billing</p>
        </div>
        <button onClick={() => fetchData()} className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-3 py-1.5 transition-all self-start sm:self-auto" data-testid="refresh-user-management">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /><span className="text-sm">Refresh</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {summaryCards.map((c) => (
          <Card key={c.label} className="bg-slate-900/60 border-slate-800/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">{c.label}</span>
                <div className={`p-1.5 rounded-lg ${c.bg}`}>
                  <c.icon className={`h-3.5 w-3.5 ${c.color}`} />
                </div>
              </div>
              <p className={`text-xl font-bold ${c.color}`} data-testid={`summary-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {c.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Users Table */}
      <Card className="bg-slate-900/60 border-slate-800/50">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <Users className="h-5 w-5" /> All Users
            </CardTitle>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white h-8 text-xs"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/admin/users/export`, { responseType: "blob" });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a"); a.href = url; a.download = "callintell_users_export.csv"; a.click();
                    toast.success("Users exported");
                  } catch { toast.error("Export failed"); }
                }}
                data-testid="export-users-btn"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" /> Export Users
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-white h-8 text-xs"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/admin/billing/export`, { responseType: "blob" });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a"); a.href = url; a.download = "callintell_billing_export.csv"; a.click();
                    toast.success("Billing exported");
                  } catch { toast.error("Export failed"); }
                }}
                data-testid="export-billing-btn"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" /> Export Billing
              </Button>
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  placeholder="Search by name, email, company..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  className="pl-9 bg-slate-950/60 border-slate-800/50 text-slate-100 h-9"
                  data-testid="user-search"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800/50 hover:bg-transparent">
                  <TableHead className="w-[200px]"><SortHeader field="name">User</SortHeader></TableHead>
                  <TableHead><SortHeader field="company">Company</SortHeader></TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right"><SortHeader field="credits">Balance</SortHeader></TableHead>
                  <TableHead className="text-right"><SortHeader field="total_credits_purchased">Purchased</SortHeader></TableHead>
                  <TableHead className="text-right"><SortHeader field="total_credits_used">Used</SortHeader></TableHead>
                  <TableHead className="text-right"><SortHeader field="total_calls">Calls</SortHeader></TableHead>
                  <TableHead className="text-right"><SortHeader field="minutes_analyzed">Analyzed</SortHeader></TableHead>
                  <TableHead className="text-right"><SortHeader field="emergent_credits_used">Credits Used</SortHeader></TableHead>
                  <TableHead><SortHeader field="created_at">Joined</SortHeader></TableHead>
                  <TableHead className="text-center w-[60px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-12 text-slate-500">
                      {search ? "No users match your search" : "No users registered yet"}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((u) => (
                    <TableRow key={u.id} className="border-slate-800/30 hover:bg-slate-800/20 cursor-pointer" data-testid={`user-row-${u.id}`} onClick={() => navigate(`/user-management/${u.id}`)}>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {u.logo_url ? (
                              <img src={u.logo_url} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <span className="text-xs font-bold text-slate-400">{(u.name || u.email || "?").charAt(0).toUpperCase()}</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-200 truncate">{u.name || "—"}</p>
                            <p className="text-xs text-slate-500 truncate">{u.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-400">{u.company || "—"}</TableCell>
                      <TableCell className="text-center">
                        {u.role === "admin" ? (
                          <Badge className="bg-purple-500/15 text-purple-400 border-purple-500/20 text-[10px]">Admin</Badge>
                        ) : u.is_active ? (
                          <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20 text-[10px]">Active</Badge>
                        ) : (
                          <Badge className="bg-red-500/15 text-red-400 border-red-500/20 text-[10px]">Suspended</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm font-mono text-slate-300">{(u.credits || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                      <TableCell className="text-right text-sm font-mono text-green-400/80">{(u.total_credits_purchased || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                      <TableCell className="text-right text-sm font-mono text-amber-400/80">{(u.total_credits_used || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                      <TableCell className="text-right text-sm text-slate-400">{u.total_calls}</TableCell>
                      <TableCell className="text-right text-sm text-slate-400">{u.minutes_analyzed}</TableCell>
                      <TableCell className="text-right text-sm font-mono text-cyan-400/80" data-testid={`user-emergent-credits-${u.id}`}>{(u.emergent_credits_used || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(u.created_at)}</TableCell>
                      <TableCell className="text-center">
                        {u.role !== "admin" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-white hover:bg-slate-800" data-testid={`user-actions-${u.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48 bg-[#11141D] border-white/10" onClick={(e) => e.stopPropagation()}>
                              {u.is_active ? (
                                <DropdownMenuItem
                                  onClick={() => openConfirm("suspend", u, "Suspend User", `Suspend ${u.name || u.email}? They will be unable to log in.`)}
                                  className="text-amber-400 focus:bg-white/5 focus:text-amber-400"
                                >
                                  <UserX className="h-3.5 w-3.5 mr-2" /> Suspend
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => openConfirm("activate", u, "Activate User", `Re-activate ${u.name || u.email}?`)}
                                  className="text-emerald-400 focus:bg-white/5 focus:text-emerald-400"
                                >
                                  <UserCheck className="h-3.5 w-3.5 mr-2" /> Activate
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => { setBalanceDialog({ open: true, user: u }); setBalanceAmount(""); setBalanceReason(""); }}
                                className="text-green-400 focus:bg-white/5 focus:text-green-400"
                              >
                                <Wallet className="h-3.5 w-3.5 mr-2" /> Adjust Minutes
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openConfirm("reset-password", u, "Reset Password", `Reset ${u.name || u.email}'s password to TempPass123!?`)}
                                className="text-blue-400 focus:bg-white/5 focus:text-blue-400"
                              >
                                <KeyRound className="h-3.5 w-3.5 mr-2" /> Reset Password
                              </DropdownMenuItem>
                              {u.agreement_pdf_url && (
                                <DropdownMenuItem
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const res = await axios.get(`${API}/admin/users/${u.id}/agreement`, { responseType: "blob" });
                                      const url = URL.createObjectURL(res.data);
                                      const a = document.createElement("a"); a.href = url; a.download = `agreement_${u.name || u.id}.pdf`; a.click();
                                      URL.revokeObjectURL(url);
                                      toast.success("Agreement downloaded");
                                    } catch { toast.error("Failed to download agreement"); }
                                  }}
                                  className="text-cyan-400 focus:bg-white/5 focus:text-cyan-400"
                                  data-testid={`download-agreement-${u.id}`}
                                >
                                  <FileCheck className="h-3.5 w-3.5 mr-2" /> Download Agreement
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator className="bg-white/5" />
                              <DropdownMenuItem
                                onClick={() => openConfirm("purge-calls", u, "Purge Call Data", `Delete ALL calls, publishers, and callers for ${u.name || u.email}? The user account and minutes will be kept.`)}
                                className="text-orange-400 focus:bg-white/5 focus:text-orange-400"
                                data-testid={`purge-calls-${u.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Purge Call Data
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openConfirm("delete", u, "Delete User", `Permanently delete ${u.name || u.email} and ALL their data? This cannot be undone.`)}
                                className="text-red-400 focus:bg-white/5 focus:text-red-400"
                              >
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      <PaginationControls currentPage={currentPage} totalPages={Math.max(1, Math.ceil(filtered.length / perPage))} totalItems={filtered.length} setCurrentPage={setCurrentPage} testIdPrefix="users-pagination" />

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(o) => !o && setConfirmDialog({ open: false, action: null, user: null, title: "", desc: "" })}>
        <DialogContent className="bg-[#11141D] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100">{confirmDialog.title}</DialogTitle>
            <DialogDescription className="text-slate-400 pt-2">{confirmDialog.desc}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setConfirmDialog({ open: false, action: null, user: null, title: "", desc: "" })} className="text-slate-400">
              Cancel
            </Button>
            <Button
              onClick={() => doAction(confirmDialog.action, confirmDialog.user)}
              disabled={actionLoading}
              className={
                confirmDialog.action === "delete" || confirmDialog.action === "purge-calls"
                  ? "bg-red-600 hover:bg-red-500 text-white"
                  : confirmDialog.action === "suspend"
                  ? "bg-amber-600 hover:bg-amber-500 text-white"
                  : "bg-blue-600 hover:bg-blue-500 text-white"
              }
              data-testid="confirm-action-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Balance Adjustment Dialog */}
      <Dialog open={balanceDialog.open} onOpenChange={(o) => !o && setBalanceDialog({ open: false, user: null })}>
        <DialogContent className="bg-[#11141D] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Adjust Minutes</DialogTitle>
            <DialogDescription className="text-slate-400 pt-1">
              {balanceDialog.user?.name || balanceDialog.user?.email} — Current: {(balanceDialog.user?.credits || 0).toLocaleString()} minutes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400 uppercase tracking-wider font-medium">Amount (positive to add, negative to deduct)</label>
              <Input
                type="number"
                step="0.01"
                value={balanceAmount}
                onChange={(e) => setBalanceAmount(e.target.value)}
                placeholder="e.g. 50.00 or -10.00"
                className="bg-slate-950/60 border-slate-800/50 text-slate-100"
                data-testid="balance-amount"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400 uppercase tracking-wider font-medium">Reason (optional)</label>
              <Input
                value={balanceReason}
                onChange={(e) => setBalanceReason(e.target.value)}
                placeholder="e.g. Promo minutes, refund, manual charge"
                className="bg-slate-950/60 border-slate-800/50 text-slate-100"
                data-testid="balance-reason"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setBalanceDialog({ open: false, user: null })} className="text-slate-400">
              Cancel
            </Button>
            <Button
              onClick={handleBalanceSubmit}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-500 text-white"
              data-testid="submit-balance-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
